import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CHART_FIXED_HEIGHT,
  CHART_WIDGET_HEIGHT,
  formatAudSmart,
  formatCount,
  getChartPlotMargin,
  getChartYAxisProps,
  getCostAxisScale,
  getCostChartTickInterval,
  getCountAxisScale,
} from "pages/ServiceProvider/SpSetting/AiCreditsUsage/aiUsageChartUtils";
import { ChartMetricStrip, ChartWidgetState } from "pages/ServiceProvider/SpSetting/AiCreditsUsage/ChartWidgetBody";
import { useChartAxisLayout } from "pages/ServiceProvider/SpSetting/AiCreditsUsage/useChartAxisLayout";
import { aiUsageTrendSubtitle, pivotBreakdownTrends } from "./aiUsageOverview.utils";

const TrendTooltip = ({ active, payload, label, valueFormatter }) => {
  if (!active || !payload?.length) return null;

  const rows = payload
    .filter((entry) => Number(entry.value) > 0)
    .sort((a, b) => Number(b.value) - Number(a.value));

  if (!rows.length) return null;

  return (
    <div className="min-w-[210px] rounded-xl border border-gray-200 bg-white px-3.5 py-3 text-xs shadow-lg">
      <p className="font-semibold text-gray-900">{label}</p>
      <div className="mt-2 space-y-1.5 text-gray-600">
        {rows.map((entry) => (
          <p key={entry.name}>
            {entry.name}:{" "}
            <span className="font-medium text-gray-900">
              {valueFormatter(entry.value)}
            </span>
          </p>
        ))}
      </div>
    </div>
  );
};

const PlatformAiUsageBreakdownTrendChart = ({
  title,
  subtitle,
  icon: Icon,
  iconClassName = "bg-violet-50 text-violet-600",
  trends = [],
  keyField,
  from,
  to,
  granularity = "day",
  loading = false,
  error = "",
  valueKey = "call_count",
  labelFn,
  colorFn,
  topN = 5,
  metricItems = [],
  emptyTitle = "No usage in this period",
  emptyHint = "Trend data appears once AI calls are logged in this range.",
}) => {
  const valueFormatter = valueKey === "cost_aud" ? formatAudSmart : formatCount;
  const isMonthlyView = granularity === "month";
  const isWeeklyView = granularity === "week";

  const { chartData, keys } = useMemo(
    () =>
      pivotBreakdownTrends({
        trends,
        keyField,
        from,
        to,
        granularity,
        topN,
        valueKey,
        labelFn,
        colorFn,
      }),
    [trends, keyField, from, to, granularity, topN, valueKey, labelFn, colorFn]
  );

  const hasUsage = chartData.some((row) => row.total > 0);
  const { containerRef, compact } = useChartAxisLayout();
  const yAxisScale = useMemo(() => {
    const values = chartData.map((row) => row.total);
    return valueKey === "cost_aud"
      ? getCostAxisScale(values, compact)
      : getCountAxisScale(values, compact);
  }, [chartData, compact, valueKey]);
  const xAxisTickInterval = getCostChartTickInterval(chartData.length, granularity);

  return (
    <div
      className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
      style={{ height: CHART_WIDGET_HEIGHT }}
    >
      <div className="border-b border-gray-100 px-5 pt-4 pb-3">
        <div className="flex items-start gap-3">
          {Icon ? (
            <span
              className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${iconClassName}`}
            >
              <Icon className="h-5 w-5" />
            </span>
          ) : null}
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            <p className="text-xs text-gray-500">
              {subtitle || aiUsageTrendSubtitle(granularity)}
              {from && to ? ` · ${from} → ${to}` : ""}
            </p>
          </div>
        </div>
      </div>

      {!loading && !error && metricItems.length > 0 ? (
        <ChartMetricStrip items={metricItems} />
      ) : null}

      <div
        ref={containerRef}
        className="flex min-w-0 flex-1 flex-col px-4 pt-2 pb-4 sm:px-5"
      >
        <ChartWidgetState
          loading={loading}
          error={error}
          empty={!loading && !error && (!hasUsage || keys.length === 0)}
          emptyTitle={emptyTitle}
          emptyHint={emptyHint}
          skeletonVariant="bar"
        >
          <div className="min-w-0 w-full" style={{ height: CHART_FIXED_HEIGHT }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={getChartPlotMargin({
                  angledXAxis: isMonthlyView || isWeeklyView,
                  extraRight: 8,
                })}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{
                    fontSize: isMonthlyView || isWeeklyView ? 9 : 10,
                    fill: "#6b7280",
                  }}
                  axisLine={false}
                  tickLine={false}
                  interval={xAxisTickInterval}
                  angle={isMonthlyView ? -42 : isWeeklyView ? -24 : 0}
                  textAnchor={isMonthlyView || isWeeklyView ? "end" : "middle"}
                  height={isMonthlyView ? 56 : isWeeklyView ? 48 : 36}
                  dy={isMonthlyView || isWeeklyView ? 4 : 8}
                />
                <YAxis
                  {...getChartYAxisProps(yAxisScale, {
                    compact,
                    allowDecimals: valueKey === "cost_aud" && yAxisScale.step < 1,
                  })}
                />
                <Tooltip
                  content={
                    <TrendTooltip valueFormatter={valueFormatter} />
                  }
                />
                {keys.map((entry) => (
                  <Bar
                    key={entry.id}
                    dataKey={entry.dataKey}
                    name={entry.label}
                    stackId="trend"
                    fill={entry.color}
                    radius={entry.id === keys[keys.length - 1]?.id ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          {keys.length > 0 ? (
            <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-gray-600">
              {keys.map((entry) => (
                <span key={entry.id} className="inline-flex items-center gap-1.5">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-sm"
                    style={{ backgroundColor: entry.color }}
                  />
                  {entry.label}
                </span>
              ))}
            </div>
          ) : null}
        </ChartWidgetState>
      </div>
    </div>
  );
};

export default PlatformAiUsageBreakdownTrendChart;
